/**
 * Fleet secret-store registry (ADR-0017 D5) — the machine-readable inventory of
 * EVERY internal secret store across Maestro, MOS, Closer (hermes-agent), and
 * Marvin (webemo-hermes-agent), rendered by the Control Center "Secret Stores"
 * page as web content.
 *
 * HARD RULE — NEVER PUT A SECRET VALUE HERE. Only store identities, locations,
 * key NAMES / categories, auth methods, and lifecycle flags. A lint gate
 * (`scripts/lint-secret-registry.mjs`) fails the build if a value-shaped string
 * appears (the no-secret-echo discipline of ADR-0014, made executable).
 *
 * Authored as typed TS (not YAML) so the Vite app imports it directly with type
 * safety and no extra parser dependency; it is still the single machine-readable
 * source of truth and is edited by PR + the lint gate.
 */

export type SecretSystem = 'maestro' | 'mos' | 'closer' | 'marvin' | 'shared';

export type SecretStoreType =
  | 'hashicorp-vault'
  | 'vaultwarden'
  | 'k8s-secret'
  | 'age-file'
  | 'env-file'
  | 'gitea-ci'
  | 'maestro-file-vault';

export type AuthMethod =
  | 'approle'
  | 'k8s-auth'
  | 'token'
  | 'oidc'
  | 'passphrase'
  | 'machine-key'
  | 'ci-secret'
  | 'none';

/** Lifecycle / risk flags surfaced as badges in the dashboard. */
export type LifecycleFlag = 'plaintext-risk' | 'rotation-due' | 'has-ttl' | 'url-drift';

export interface SecretStoreEntry {
  /** Owning system. */
  system: SecretSystem;
  type: SecretStoreType;
  /** Canonical, non-secret address/path (e.g. a Vault path or k8s secret name). */
  location: string;
  /** Key NAMES or categories only — NEVER values. */
  keys: string[];
  auth: AuthMethod;
  /** Where it's referenced in code/config (non-secret), best-effort. */
  referencedAt?: string;
  flags?: LifecycleFlag[];
  note?: string;
}

export const SECRET_STORE_TYPE_LABELS: Record<SecretStoreType, string> = {
  'hashicorp-vault': 'HashiCorp Vault',
  vaultwarden: 'Vaultwarden',
  'k8s-secret': 'Kubernetes Secret',
  'age-file': 'age-encrypted file',
  'env-file': '.env file',
  'gitea-ci': 'Gitea CI secret',
  'maestro-file-vault': 'Maestro file-vault',
};

export const SYSTEM_LABELS: Record<SecretSystem, string> = {
  maestro: 'Maestro',
  mos: 'MOS',
  closer: 'Closer (hermes-agent)',
  marvin: 'Marvin (webemo-hermes-agent)',
  shared: 'Shared',
};

/**
 * Seeded from the 2026-06-14 fleet survey (ADR-0017). Names/categories only.
 * Canonical Vault address is `http://10.99.0.1:8200`; `10.0.0.1` is
 * decommissioned and `127.0.0.1` is an operator SSH-tunnel convenience, both
 * flagged `url-drift` where still referenced.
 */
export const SECRET_STORE_REGISTRY: SecretStoreEntry[] = [
  // --- Maestro ---
  {
    system: 'maestro',
    type: 'hashicorp-vault',
    location: 'secret/maestro/env',
    keys: ['DATABASE_URL', 'POSTGRES_PASSWORD', 'OPENAI_API_KEY', 'CLAUDE_API_KEY', 'TELEGRAM_BOT_TOKEN', 'COOKIE_SECRET', 'audit-HMAC'],
    auth: 'k8s-auth',
    referencedAt: 'Maestro k8s/externalsecret.yaml',
    note: 'Synced into the maestro-env k8s Secret via ExternalSecret.',
  },
  {
    system: 'maestro',
    type: 'hashicorp-vault',
    location: 'secret/maestro/operator',
    keys: ['org_id'],
    auth: 'approle',
    referencedAt: 'Maestro deploy/vault/policies/maestro-operator-org.hcl',
    note: 'Non-secret operator org id; scoped AppRole (path-level least-privilege).',
  },
  {
    system: 'maestro',
    type: 'hashicorp-vault',
    location: 'secret/gitea',
    keys: ['maestro_bot_token', 'bot_provisioning_token'],
    auth: 'token',
    referencedAt: 'Maestro scripts/launch-with-vault-secrets.sh',
  },
  {
    system: 'maestro',
    type: 'k8s-secret',
    location: 'maestro-env (ns: maestro)',
    keys: ['(materialized from secret/maestro/env)'],
    auth: 'k8s-auth',
    note: 'Derived from Vault via ExternalSecret — not authored here.',
  },
  {
    system: 'maestro',
    type: 'gitea-ci',
    location: 'Gitea Actions secrets',
    keys: ['MAESTRO_PRIME_PACKAGE_TOKEN', 'MAESTRO_PRIME_PUBLISHER_USER', 'DOCKERHUB_USER', 'DOCKERHUB_TOKEN', 'MAESTRO_PRIME_VAULT_TOKEN', 'REGISTRY_BROKER_HMAC_SECRET'],
    auth: 'ci-secret',
    referencedAt: 'Maestro .gitea/workflows/*',
    flags: ['rotation-due'],
    note: 'Long-lived; ADR-0014 plans OIDC cutover.',
  },
  {
    system: 'maestro',
    type: 'maestro-file-vault',
    location: 'ARCHON_VAULT_PATH (~/.maestro/vault)',
    keys: ['provider credentials (per-codebase AI/git tokens)'],
    auth: 'machine-key',
    referencedAt: 'Maestro packages/core/src/security/*-vault.ts',
  },

  // --- MOS ---
  {
    system: 'mos',
    type: 'hashicorp-vault',
    location: 'secret/shared/*',
    keys: ['JWT_SECRET', 'SESSION_SECRET', 'SUPERTOKENS_API_KEY', 'database passwords', 'Stripe keys', 'AI provider keys'],
    auth: 'k8s-auth',
    referencedAt: 'MOS CENTRALIZED_SECRETS_PLAN.md',
  },
  {
    system: 'mos',
    type: 'hashicorp-vault',
    location: 'secret/mos/production/oidc/signing-key',
    keys: ['RS256 key pair (current + previous)'],
    auth: 'k8s-auth',
    note: 'OIDC issuer signing key (SuperTokens / central auth).',
  },
  {
    system: 'mos',
    type: 'hashicorp-vault',
    location: 'secret/apis/*',
    keys: ['RESEND_API_KEY', 'grafana admin-user/admin-password'],
    auth: 'k8s-auth',
    referencedAt: 'MOS k8s/secrets/externalsecret-*.yaml',
  },
  {
    system: 'mos',
    type: 'k8s-secret',
    location: 'grafana-admin, alertmanager-smtp, vault-token-reviewer',
    keys: ['(materialized from Vault via ESO)'],
    auth: 'k8s-auth',
    referencedAt: 'MOS k8s/secrets/cluster-secret-store-vault.yaml',
  },
  {
    system: 'mos',
    type: 'env-file',
    location: '.env / .env.shared / .env.hetzner.local (dev/host)',
    keys: ['POSTGRES_*', 'REDIS_*', 'JWT_*', 'STRIPE_*', 'ANTHROPIC_API_KEY', 'OPENAI_API_KEY', 'ZOHO_*'],
    auth: 'none',
    flags: ['plaintext-risk'],
    note: 'Dev/host config; must be gitignored. Vault is the deployed source.',
  },

  // --- Closer (hermes-agent) ---
  {
    system: 'closer',
    type: 'env-file',
    location: 'hermes-agent .env + ~/.hermes/config.yaml',
    keys: ['LLM provider keys (OpenRouter/Gemini/…)', 'SLACK_*', 'TELEGRAM_BOT_TOKEN', 'BROWSERBASE_*', 'GITHUB_*', 'TERMINAL_SSH_KEY'],
    auth: 'none',
    flags: ['plaintext-risk'],
    note: 'No Vault integration today — all credentials in env (Phase: bring onto the secret lane).',
  },

  // --- Marvin (webemo-hermes-agent) ---
  {
    system: 'marvin',
    type: 'hashicorp-vault',
    location: 'secret/motel-ashburn-1/credentials',
    keys: ['per-property runtime credentials'],
    auth: 'approle',
    referencedAt: 'webemo-hermes-agent .env.vault',
    flags: ['has-ttl', 'url-drift'],
    note: 'Reached via 127.0.0.1 SSH tunnel (operator convenience, not the architecture address).',
  },
  {
    system: 'marvin',
    type: 'env-file',
    location: 'webemo-hermes-agent .env.vault + per-property .hermes-*/.env',
    keys: ['VAULT_ROLE_ID', 'VAULT_SECRET_ID', 'per-property DB/Telegram/encryption keys'],
    auth: 'none',
    flags: ['plaintext-risk', 'rotation-due'],
    note: 'AppRole role_id/secret_id stored PLAINTEXT — rotate and move to Vault + Vaultwarden (ADR-0017 Phase 0).',
  },

  // --- Shared / break-glass ---
  {
    system: 'shared',
    type: 'vaultwarden',
    location: 'https://vault.mahoosuc.ai',
    keys: ['Vault recovery keys', 'emergency admin token', 'operator AppRole passphrase'],
    auth: 'passphrase',
    note: 'Break-glass / bootstrap ONLY (biometric). NOT HashiCorp Vault; never a runtime source.',
  },
  {
    system: 'shared',
    type: 'age-file',
    location: '~/.config/mos/vault-approle.age',
    keys: ['operator AppRole role_id + secret_id (age-encrypted)'],
    auth: 'passphrase',
    flags: ['rotation-due'],
    note: 'scrypt passphrase-encrypted; the bootstrap credential the auth-vault wrapper decrypts.',
  },
];
